import * as React from 'react';

// Component
import ThirdpartyLoginMethods from '@tob/shared/thirdparty-auth/features/login/ThirdpartyLoginMethods';
import LoginByPasswordForm from '../../../LoginByPasswordForm';
import InputAuthKey from '../../../InputAuthKey';
import { LoginInputPassword } from '@shopee-user/shared-customization-ui';
import LoginTermsOfService from '../../../LoginTermsOfService';
import Button from '../../../Button';
import Logo from '../../../Logo';
import { Link } from 'react-router-dom';
import AltLoginLink from '../../../AltLoginLink';

// Page Context
import { useLoginByPasswordRequirePasswordContext } from '@tob/pages/login-by-password/containers/RequirePasswordV2/hook';

// Utils, Hooks, HOCs
import { isSupportThirdpartyOAuth } from '@tob/shared/thirdparty-auth/utils';
import { useTranslation } from '@shopee-user/platform';
import { isFeatureEnabled } from '@shopee-user/spx-customization-logic';
import { Feature } from '@shopee-user/feature-toggle';
import { APIProgress } from '@shopee-user/api-constants';
import { Layout, useResponsiveLayout } from '@shopee-user/layout-responsive';
import { forwardOrAppendAuthParamsIfNotExist } from '@shopee-user/authentication-utils';
import { loginByOTPURL } from '@shopee-user/url';
import { LoginByPasswordSkeletonTrackingUtils } from '@shopee-user/business-customization-types';

import styles from './style.scss';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';

function Content({ trackingUtils }: LoginByPasswordSkeletonTrackingUtils) {
  const I18n = useTranslation();
  const {
    state,
    components: { ResetPasswordLink },
    onSubmitForm,
    onThirdpartyLoginSuccess,
  } = useLoginByPasswordRequirePasswordContext();
  const { layout } = useResponsiveLayout();

  const MobileLogo = layout === Layout.Mobile && (
    <div className={styles.logoContainer}>
      <Logo className={styles.logo} />
    </div>
  );

  const MobileAltLoginLink = layout === Layout.Mobile && <AltLoginLink />;
  return (
    <div>
      {MobileLogo}
      <div className={styles.formContainer}>
        <LoginByPasswordForm
          formInitial={{ authKey: state.initialAuthKey }}
          submitOnEnterPress={!state.shouldShowCaptcha}
          onSubmit={onSubmitForm}
        >
          {({ form }) => {
            const { authKey, password } = form.fields;
            return (
              <div>
                <InputAuthKey
                  {...authKey}
                  errors={authKey.errors.map(key => I18n.t(key))}
                  wrapperClassName={styles.inputAuthKey}
                />
                <LoginInputPassword
                  placeholder={I18n.t('label_auth_password')}
                  showError
                  showErrorOutline
                  {...password}
                  errors={password.errors.map(key => I18n.t(key))}
                  extraContent={
                    isFeatureEnabled(Feature.ResetPasswordEntrance) ||
                    isFeatureEnabled(
                      Feature.ResetPasswordHandledByBusinessPartner
                    ) ? (
                      <ResetPasswordLink />
                    ) : null
                  }
                />
                <Button
                  className={styles.submitButton}
                  text={I18n.t('label_auth_log_in')}
                  disabled={
                    state.apiState.progress === APIProgress.Requested ||
                    authKey.value.length === 0 ||
                    password.value.length === 0 ||
                    authKey.errors.length > 0 ||
                    password.errors.length > 0 ||
                    state.shouldShowCaptcha
                  }
                  onClick={form.onSubmit}
                />
              </div>
            );
          }}
        </LoginByPasswordForm>
        <div className={styles.extraLinks}>
          {isFeatureEnabled(Feature.LoginByOtpEntrance) && (
            <div className={styles.loginWithSMSDiv}>
              <Link
                className={styles.link}
                to={forwardOrAppendAuthParamsIfNotExist(loginByOTPURL.getUrl())}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.stopPropagation();
                  }
                }}
                onClick={() =>
                  trackingUtils.trackClickRedirectToLoginWithPhoneButton?.()
                }
              >
                {I18n.t('label_auth_login_with_sms')}
              </Link>
            </div>
          )}
          {isSupportThirdpartyOAuth() && (
            <ThirdpartyLoginMethods
              entryPoint={ThirdpartyBindingEntryPoint.LoginByPasswordPage}
              className={styles.thirdpartyLoginMethods}
              dividerClassName={styles.thirdpartyLoginMethodsDivider}
              onLoginSuccess={onThirdpartyLoginSuccess}
            />
          )}
        </div>
        {isFeatureEnabled(Feature.LoginTermsOfService) && (
          <LoginTermsOfService />
        )}
        {MobileAltLoginLink}
      </div>
    </div>
  );
}

export default Content;
